import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Typography } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import { getAwaitingReplacementVendorReturns } from '../../api/vendorReturn';
import { getVendors } from '../../api/vendor';
import { formatQty, formatCurrency } from '../../utils';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export default function VendorReturnAwaitingReplacement() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const base = url.replace(/\/vendor-returns\/awaiting-replacement$/, '');

  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const [data, vendorsData] = await Promise.all([
        getAwaitingReplacementVendorReturns(),
        getVendors().catch(() => []),
      ]);
      setItems(normalizeList(data));
      setVendors(normalizeList(vendorsData));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function resolveVendorName(vendorId) {
    if (!vendorId) return 'Unknown Vendor';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  const groups = useMemo(() => {
    const byVendor = new Map();
    items.forEach((item) => {
      const key = item.vendor_id || 'unknown';
      if (!byVendor.has(key)) byVendor.set(key, []);
      byVendor.get(key).push(item);
    });
    return Array.from(byVendor.entries()).map(([vendorId, rows]) => ({ vendorId, rows }));
  }, [items]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error" action={<InvButton size="small" onClick={load}>Retry</InvButton>}>{error}</Alert></Box>;

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Vendor Returns"
        title="Awaiting Replacement"
        description={`${items.length} line${items.length === 1 ? '' : 's'} awaiting replacement from vendors`}
        actions={
          <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(`${base}/vendor-returns`)}>
            Back to Vendor Returns
          </InvButton>
        }
      />

      <Alert severity="info" sx={{ mb: 2.5 }}>
        Tracking-only view — there is currently no backend action to mark a line as received here.
        Receive the physical replacement stock via Rejected Items → Create Replacement GRN.
      </Alert>

      {!groups.length ? (
        <SectionCard>
          <Typography sx={{ color: IPD_COLORS.textSecondary, py: 2, textAlign: 'center' }}>
            No replacements currently awaited.
          </Typography>
        </SectionCard>
      ) : groups.map(({ vendorId, rows }) => (
        <SectionCard key={vendorId} title={resolveVendorName(vendorId)} subtitle={`${rows.length} line${rows.length === 1 ? '' : 's'}`} sx={{ mb: 2.5 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Return</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Return Qty</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Unit Rate</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row.vendor_return_item_id || i}>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ color: IPD_COLORS.primary, cursor: 'pointer', fontWeight: 600 }}
                        onClick={() => history.push(`${base}/vendor-return/${row.vendor_return_id}`)}
                      >
                        {row.return_number || row.vendor_return_id}
                      </Box>
                    </TableCell>
                    <TableCell>{row.item_id}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.batch_number || '—'}</TableCell>
                    <TableCell align="right">{formatQty(row.return_quantity)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.unit_rate)}</TableCell>
                    <TableCell><StatusChip status={row.replacement_status || 'AWAITING'} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Not yet available — pending backend endpoint">
                        <span>
                          <InvButton size="small" variant="outlined" tone="teal" disabled>
                            Mark Received
                          </InvButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </SectionCard>
      ))}
    </Box>
  );
}
